import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ClassForm, ClassFormValues } from '@/components/classes/ClassForm';
import { ClassMutationService } from '@/services/classService';
import { useAuth } from '@/hooks/useAuth';
import { useOrganization } from '@/hooks/useOrganization';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { ArrowLeft, BookOpen, Plus, X, Loader2 } from 'lucide-react';
import { getLocations } from '@/services/locationService';
import { getSeasons } from '@/services/seasonService';
import { motion } from 'framer-motion';

const NewClassPage: React.FC = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { organization, loading: orgLoading } = useOrganization();
  const queryClient = useQueryClient();
  const [locationOptions, setLocationOptions] = useState<{ value: string; label: string }[]>([]);
  const [seasonOptions, setSeasonOptions] = useState<{ value: string; label: string }[]>([]);
  const [optionsLoading, setOptionsLoading] = useState(true);

  const organizationId = user?.user_metadata?.organization_id;

  useEffect(() => {
    const fetchOptions = async () => {
      if (!organizationId) return;
      setOptionsLoading(true);
      try {
        const [locations, seasons] = await Promise.all([
          getLocations(organizationId),
          getSeasons(organizationId),
        ]);
        setLocationOptions(locations.map(loc => ({ value: loc.id, label: loc.name })));
        setSeasonOptions(seasons.map(season => ({ value: season.id, label: season.name })));
      } catch (err) {
        toast.error('Failed to load location or season options');
      } finally {
        setOptionsLoading(false);
      }
    };
    fetchOptions();
  }, [organizationId]);

  const addClassMutation = useMutation({
    mutationFn: (values: any) => {
      if (!organizationId) {
        throw new Error('No organization ID found. Please log in again.');
      }
      
      // Convert empty string UUIDs to null
      const location_id = !values.location_id || values.location_id === '' || values.location_id === 'none' || values.location_id === 'virtual' || values.location_id === 'offsite' ? null : values.location_id;
      const season_id = !values.season_id || values.season_id === '' ? null : values.season_id;

      // Include ALL fields that the form supports
      // Note: ClassForm already converts price_dollars to price_cents, so we use the converted value
      const classData = {
        name: values.name,
        description: values.description || '',
        location_id,
        max_students: values.capacity,
        organization_id: organizationId,
        is_event: false,
        billing_model: values.billing_model,
        price_cents: values.price_cents, // Use the already converted price_cents from ClassForm
        season_id,
        level: values.level || '',
        dress_code: values.dress_code || '',
        category: values.category || '',
        status: values.status || 'active',
        trial_available: values.trial_available || false,
        is_active: values.is_active !== undefined ? values.is_active : true,
        waitlist_enabled: values.waitlistEnabled !== undefined ? values.waitlistEnabled : true,
        schedules: values.schedules || [],
      };
      
      return ClassMutationService.createClass(classData);
    },
    onSuccess: (createdClass) => {
      toast.success('Class created successfully!');
      // Invalidate classes query to refresh the list
      queryClient.invalidateQueries({ queryKey: ['classes'] });
      
      if (createdClass && createdClass.id) {
        navigate('/admin/classes');
      } else {
        navigate('/admin/classes');
      }
    },
    onError: (error: any) => {
      console.error('Error creating class:', error);
      toast.error(error.message || 'Failed to create class. Please try again.');
    },
  });

  const onSubmitClass = (values: any) => {
    addClassMutation.mutate(values);
  };

  const handleCancel = () => {
    navigate('/admin/classes');
  };

  // Create default values using organization class defaults
  const getDefaultValues = () => {
    const classDefaults = organization?.class_defaults || {};
    
    return {
      name: '',
      description: '',
      location_id: 'none',
      capacity: classDefaults.max_students || 1,
      season_id: 'no-season',
      billing_model: 'monthly' as const,
      price_dollars: '',
      schedule_type: 'recurring_pattern' as const,
      schedules: [
        { weekday: 1, start_time: '17:00', end_time: '18:00', rrule: '' },
      ],
      recurrence: {
        type: 'weekly',
        interval: 1,
        end: 'never',
        count: 1,
        until: '',
      },
      waitlistEnabled: classDefaults.waitlist_enabled ?? true,
    };
  };

  if (optionsLoading || orgLoading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
        <div className="container mx-auto py-8">
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <Loader2 className="h-8 w-8 animate-spin text-green-600 mx-auto mb-4" />
              <p className="text-gray-600">Loading form options...</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50">
      <div className="container mx-auto py-8 px-4">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-8"
        >
          <div className="flex items-center gap-4 mb-4">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleCancel}
              className="text-gray-600 hover:text-gray-900"
            >
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Classes
            </Button>
          </div>
          
          <div className="flex items-center gap-4">
            <div className="p-3 rounded-xl bg-gradient-to-br from-green-100 to-blue-100 shadow-sm">
              <Plus className="h-8 w-8 text-green-600" />
            </div>
            <div>
              <h1 className="text-3xl font-bold text-gray-900">Create New Class</h1>
              <p className="text-gray-600 mt-1">Set up a new class with schedule, pricing, and details</p>
            </div>
          </div>
        </motion.div>

        {/* Form Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="max-w-4xl mx-auto"
        >
          <Card className="shadow-xl border-0 bg-white/80 backdrop-blur-sm">
            <CardHeader className="bg-gradient-to-r from-green-600 to-blue-600 text-white rounded-t-lg">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <BookOpen className="h-6 w-6" />
                  <div>
                    <CardTitle className="text-xl">New Class Setup</CardTitle>
                    <p className="text-green-100 text-sm">Fill in the details to create your new class</p>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={handleCancel}
                    disabled={addClassMutation.isPending}
                    className="text-white hover:bg-white/10"
                  >
                    <X className="h-4 w-4 mr-2" />
                    Cancel
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="p-8">
              <ClassForm
                onSubmit={onSubmitClass}
                onCancel={handleCancel}
                isSubmitting={addClassMutation.isPending}
                defaultValues={getDefaultValues()}
                locationOptions={locationOptions}
                seasonOptions={seasonOptions}
              />
            </CardContent>
          </Card>
        </motion.div>
      </div>
    </div>
  );
};

export default NewClassPage; 